import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const NotificationContext = createContext(null);

function playDing() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc1 = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    const gain = ctx.createGain();
    osc1.connect(gain); osc2.connect(gain); gain.connect(ctx.destination);
    osc1.type = 'sine'; osc1.frequency.setValueAtTime(1047, ctx.currentTime);
    osc2.type = 'sine'; osc2.frequency.setValueAtTime(1319, ctx.currentTime);
    gain.gain.setValueAtTime(0.25, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6);
    osc1.start(ctx.currentTime); osc1.stop(ctx.currentTime + 0.6);
    osc2.start(ctx.currentTime + 0.08); osc2.stop(ctx.currentTime + 0.6);
    osc1.onended = () => { try { ctx.close(); } catch {} };
  } catch {}
}

export function NotificationProvider({ user, children }) {
  const [notifications, setNotifications] = useState([]);
  const [count, setCount] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    try { return localStorage.getItem('notif_sound_enabled') !== 'false'; } catch { return true; }
  });
  const soundEnabledRef = useRef(soundEnabled);
  useEffect(() => { soundEnabledRef.current = soundEnabled; }, [soundEnabled]);

  const toggleSound = () => {
    setSoundEnabled(prev => {
      const next = !prev;
      try { localStorage.setItem('notif_sound_enabled', next ? 'true' : 'false'); } catch {}
      return next;
    });
  };

  const fetchNotifications = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(40);
      const list = data || [];
      setNotifications(list);
      setCount(list.filter(n => !n.read).length);
    } catch {}
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    fetchNotifications();

    const channel = supabase
      .channel(`notif-ctx-${user.id}`)
      .on('postgres_changes', {
        event: 'INSERT', schema: 'public', table: 'notifications',
        filter: `user_id=eq.${user.id}`,
      }, (payload) => {
        setNotifications(prev => [payload.new, ...prev].slice(0, 40));
        setCount(n => n + 1);
        if (soundEnabledRef.current) playDing();
      })
      .on('postgres_changes', {
        event: 'UPDATE', schema: 'public', table: 'notifications',
        filter: `user_id=eq.${user.id}`,
      }, () => fetchNotifications())
      .subscribe();

    const interval = setInterval(fetchNotifications, 15000);

    return () => {
      clearInterval(interval);
      supabase.removeChannel(channel);
    };
  }, [user?.id, fetchNotifications]);

  const markAllRead = useCallback(async () => {
    if (!user?.id) return;
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setCount(0);
    await fetch('/api/notifications/mark-read', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId: user.id }),
    }).catch(() => {});
  }, [user?.id]);

  const markOneRead = useCallback(async (id) => {
    setNotifications(prev => {
      const updated = prev.map(n => n.id === id ? { ...n, read: true } : n);
      setCount(updated.filter(n => !n.read).length);
      return updated;
    });
    await fetch('/api/notifications/mark-one-read', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ notifId: id, userId: user.id }),
    }).catch(() => {});
  }, [user?.id]);

  return (
    <NotificationContext.Provider value={{
      notifications, count, soundEnabled, toggleSound,
      markAllRead, markOneRead, refresh: fetchNotifications,
    }}>
      {children}
    </NotificationContext.Provider>
  );
}

export const useNotifications = () => useContext(NotificationContext);
